import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FiCalendar } from "react-icons/fi";
import moment from "moment";
import toast from "react-hot-toast";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import {
  createRelativeRegisterFormApi,
  updateRelativeRegisterFormApi,
  getRelativeRegisterFormByIdApi,
} from "../../api/finance.collaborator.api";
import { extractErrorMessage } from "../../api/client";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#DCE1EA";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const GRAD = "linear(to-r, #68A2FF, #2168DA)";

// Khớp collaborator.constant.ts
const GENDER_TYPE = [
  { id: "0", name: "Nam" },
  { id: "1", name: "Nữ" },
  { id: "2", name: "Khác" },
];
const PLACE_OF_ISSUE_TYPE = [
  { id: "1", name: "Cục Cảnh sát quản lý hành chính về trật tự xã hội" },
  { id: "2", name: "Cục Cảnh sát đăng ký quản lý cư trú và dữ liệu Quốc gia về dân cư" },
  { id: "3", name: "Bộ công an" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Label = ({ children, required }) => (
  <Text fontSize="13px" fontWeight={600} mb="6px" color={TEXT}>
    {children}{required && <Text as="span" color="#EF2E14"> *</Text>}
  </Text>
);
const ErrText = ({ error }) => (error ? <Text fontSize="12px" color="red.500" mt="4px">{error}</Text> : null);

const TextField = ({ label, required, value, onChange, placeholder, uppercase, error }) => (
  <Box mb="16px">
    <Label required={required}>{label}</Label>
    <Input
      value={value}
      onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
      placeholder={placeholder}
      h="46px"
      fontSize="14px"
      borderColor={error ? "#EF2E14" : BORDER}
      borderRadius="8px"
    />
    <ErrText error={error} />
  </Box>
);

const SelectField = ({ label, required, value, placeholder, onOpen, error }) => (
  <Box mb="16px">
    <Label required={required}>{label}</Label>
    <Flex as="button" type="button" onClick={onOpen} align="center" justify="space-between" h="46px" px="12px" border="1px solid" borderColor={error ? "#EF2E14" : BORDER} borderRadius="8px" w="100%">
      <Text fontSize="14px" color={value ? TEXT : MUTED} noOfLines={1} textAlign="left">{value || placeholder || "Chọn..."}</Text>
      <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
    </Flex>
    <ErrText error={error} />
  </Box>
);

// Ngày (hiển thị DD/MM/YYYY, lưu DD/MM/YYYY) qua input date native ẩn phía trên
const DateField = ({ label, required, value, onChange, placeholder, error }) => {
  const iso = value && moment(value, "DD/MM/YYYY", true).isValid() ? moment(value, "DD/MM/YYYY").format("YYYY-MM-DD") : "";
  return (
    <Box mb="16px">
      <Label required={required}>{label}</Label>
      <Box position="relative" w="100%">
        <Flex align="center" justify="space-between" h="46px" px="12px" border="1px solid" borderColor={error ? "#EF2E14" : BORDER} borderRadius="8px">
          <Text fontSize="14px" color={value ? TEXT : MUTED}>{value || placeholder || "dd/mm/yyyy"}</Text>
          <FiCalendar color={MUTED} size={18} />
        </Flex>
        <Input
          type="date"
          value={iso}
          onChange={(e) => onChange(e.target.value ? moment(e.target.value, "YYYY-MM-DD").format("DD/MM/YYYY") : "")}
          position="absolute"
          inset="0"
          w="100%"
          h="100%"
          opacity={0}
          p="0"
          cursor="pointer"
        />
      </Box>
      <ErrText error={error} />
    </Box>
  );
};

const CollaboratorRequestForm = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const isEdit = params.get("type") === "edit" && !!id;

  const [f, setF] = useState({ gender: "", genderLabel: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const set = (k) => (v) => {
    setF((s) => ({ ...s, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  const sheet = useDisclosure();
  const [sheetKey, setSheetKey] = useState(null); // gender | place

  const sheetConfig = {
    gender: { title: "Giới tính", options: GENDER_TYPE, valueKey: "gender", labelKey: "genderLabel", store: "id" },
    place: { title: "Nơi cấp", options: PLACE_OF_ISSUE_TYPE, valueKey: "placeOfIdentification", labelKey: "placeLabel", store: "name" },
  };

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getRelativeRegisterFormByIdApi(id)
      .then((res) => {
        const d = res?.relativeInfo || res || {};
        const genderId = d.gender != null ? String(d.gender) : "";
        setF({
          fullName: d.fullName || "",
          phone: d.phone || "",
          email: d.email || "",
          identificationNumber: d.identificationNumber || "",
          dateOfIdentification: d.dateOfIdentification ? moment(d.dateOfIdentification).format("DD/MM/YYYY") : "",
          placeOfIdentification: d.placeOfIdentification || "",
          placeLabel: d.placeOfIdentification || "",
          permanentAddress: d.permanentAddress || "",
          currentAddress: d.currentAddress || "",
          taxCode: d.taxCode || "",
          bankAccount: d.bankAccount || "",
          dateOfBirth: d.dateOfBirth ? moment(d.dateOfBirth).format("DD/MM/YYYY") : "",
          gender: genderId,
          genderLabel: GENDER_TYPE.find((g) => g.id === genderId)?.name || "",
        });
      })
      .catch((e) => { toast.error(extractErrorMessage(e, "Lấy thông tin phiếu đăng ký CTV thất bại")); navigate(-1); })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const openSheet = (k) => { setSheetKey(k); sheet.onOpen(); };
  const pickOption = (o) => {
    const cfg = sheetConfig[sheetKey];
    const stored = cfg.store === "name" ? o.name : o.id;
    setF((s) => ({ ...s, [cfg.valueKey]: stored, [cfg.labelKey]: o.name }));
    setErrors((e) => (e[cfg.valueKey] ? { ...e, [cfg.valueKey]: undefined } : e));
    sheet.onClose();
  };

  // Validate (mirror formSetRelativeRequestValidation)
  const validate = () => {
    const e = {};
    if (f.gender === "" || f.gender == null) e.gender = "Vui lòng chọn giới tính";
    if (!f.fullName?.trim()) e.fullName = "Họ tên là trường bắt buộc";
    if (!f.dateOfBirth || !moment(f.dateOfBirth, "DD/MM/YYYY", true).isValid()) e.dateOfBirth = "Ngày sinh không hợp lệ";
    if (!f.email?.trim()) e.email = "Email là trường bắt buộc";
    else if (!EMAIL_RE.test(f.email.trim())) e.email = "Email không đúng định dạng";
    if (!f.phone?.trim()) e.phone = "Số điện thoại là trường bắt buộc";
    else if (!/^[0-9]{10,12}$/.test(f.phone.trim())) e.phone = "Số điện thoại không hợp lệ";
    if (!f.identificationNumber?.trim()) e.identificationNumber = "Số CCCD/CMND là bắt buộc";
    if (!f.dateOfIdentification || !moment(f.dateOfIdentification, "DD/MM/YYYY", true).isValid()) e.dateOfIdentification = "Ngày cấp không hợp lệ";
    if (!f.placeOfIdentification) e.placeOfIdentification = "Vui lòng chọn nơi cấp";
    if (!f.permanentAddress?.trim()) e.permanentAddress = "Địa chỉ thường trú là bắt buộc";
    if (!f.currentAddress?.trim()) e.currentAddress = "Chỗ ở hiện tại là bắt buộc";
    if (!f.bankAccount?.trim()) e.bankAccount = "Số tài khoản ngân hàng là bắt buộc";
    return e;
  };

  const toYMD = (ddmmyyyy) => moment(ddmmyyyy, "DD/MM/YYYY").format("YYYY-MM-DD");

  const onSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); toast.error(Object.values(e)[0]); return; }
    setSubmitting(true);
    try {
      const body = {
        type: "CREATE",
        relativeInfo: {
          fullName: f.fullName?.trim() || undefined,
          phone: f.phone?.trim() || undefined,
          email: f.email?.trim() || undefined,
          identificationNumber: f.identificationNumber?.trim() || undefined,
          dateOfIdentification: f.dateOfIdentification ? toYMD(f.dateOfIdentification) : undefined,
          placeOfIdentification: f.placeOfIdentification || undefined,
          permanentAddress: f.permanentAddress?.trim() || undefined,
          currentAddress: f.currentAddress?.trim() || undefined,
          taxCode: f.taxCode?.trim() || undefined,
          bankAccount: f.bankAccount?.trim() || undefined,
          dateOfBirth: f.dateOfBirth ? toYMD(f.dateOfBirth) : undefined,
          gender: Number(f.gender),
        },
      };
      if (isEdit) {
        await updateRelativeRegisterFormApi(id, body);
        toast.success("Cập nhật phiếu đăng ký CTV thành công");
      } else {
        await createRelativeRegisterFormApi(body);
        toast.success("Tạo phiếu đăng ký CTV thành công");
      }
      navigate(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu`);
    } catch (err) {
      toast.error(extractErrorMessage(err, isEdit ? "Cập nhật phiếu đăng ký CTV thất bại" : "Tạo phiếu đăng ký CTV thất bại"));
    } finally {
      setSubmitting(false);
    }
  };

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
      ) : (
        <>
          <Box flex="1" overflowY="auto" px="16px" pt="16px" pb="16px">
            <SelectField label="Giới tính" required value={f.genderLabel} placeholder="Chọn giới tính" onOpen={() => openSheet("gender")} error={errors.gender} />
            <TextField label="Họ tên" required value={f.fullName || ""} onChange={set("fullName")} placeholder="Nhập họ tên" uppercase error={errors.fullName} />
            <DateField label="Ngày sinh" required value={f.dateOfBirth || ""} onChange={set("dateOfBirth")} error={errors.dateOfBirth} />
            <TextField label="Email" required value={f.email || ""} onChange={set("email")} placeholder="Nhập email" error={errors.email} />
            <TextField label="Số điện thoại" required value={f.phone || ""} onChange={set("phone")} placeholder="Nhập số điện thoại" error={errors.phone} />
            <TextField label="Số CCCD/CMND" required value={f.identificationNumber || ""} onChange={set("identificationNumber")} placeholder="Nhập CCCD/CMND" error={errors.identificationNumber} />
            <DateField label="Ngày cấp" required value={f.dateOfIdentification || ""} onChange={set("dateOfIdentification")} error={errors.dateOfIdentification} />
            <SelectField label="Nơi cấp" required value={f.placeLabel} placeholder="Chọn nơi cấp" onOpen={() => openSheet("place")} error={errors.placeOfIdentification} />
            <TextField label="Địa chỉ thường trú" required value={f.permanentAddress || ""} onChange={set("permanentAddress")} placeholder="Nhập địa chỉ thường trú" uppercase error={errors.permanentAddress} />
            <TextField label="Chỗ ở hiện tại" required value={f.currentAddress || ""} onChange={set("currentAddress")} placeholder="Nhập chỗ ở hiện tại" uppercase error={errors.currentAddress} />
            <TextField label="Mã số thuế" value={f.taxCode || ""} onChange={set("taxCode")} placeholder="Nhập mã số thuế" uppercase error={errors.taxCode} />
            <TextField label="Số tài khoản ngân hàng" required value={f.bankAccount || ""} onChange={set("bankAccount")} placeholder="Nhập số tài khoản" uppercase error={errors.bankAccount} />
          </Box>

          <Box position="sticky" bottom="0" bg="white" borderTop="1px solid #EEF1F6" px="16px" py="12px">
            <Flex gap="16px">
              <Button flex="1" h="50px" variant="outline" borderColor={APP.primary200} color={APP.primary200} borderRadius="8px" _hover={{ bg: "#EAF2FF" }} onClick={() => navigate(-1)}>Hủy</Button>
              <Button flex="1" h="50px" bgImage={GRAD} color="white" borderRadius="8px" _hover={{ bgImage: GRAD, opacity: 0.92 }} isLoading={submitting} onClick={onSubmit}>{isEdit ? "Lưu" : "Thêm"}</Button>
            </Flex>
          </Box>
        </>
      )}

      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">{sheetKey ? sheetConfig[sheetKey].title : ""}</Text>
            <Box maxH="55vh" overflowY="auto">
              {(sheetKey ? sheetConfig[sheetKey].options : []).map((o, i, arr) => {
                const cfg = sheetConfig[sheetKey];
                const stored = cfg.store === "name" ? o.name : o.id;
                const active = f[cfg.valueKey] === stored;
                return (
                  <Flex key={o.id} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={i === arr.length - 1 ? "none" : "1px solid #EEF1F6"} onClick={() => pickOption(o)}>
                    <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400} textAlign="left">{o.name}</Text>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                  </Flex>
                );
              })}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>Đóng</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={isEdit ? "Chỉnh sửa phiếu đăng ký CTV" : "Phiếu đăng ký CTV mới"} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title={isEdit ? "Chỉnh sửa phiếu đăng ký CTV" : "Phiếu đăng ký CTV mới"} />
      {pageBody}
    </Box>
  );
};

export default CollaboratorRequestForm;
